import React, { useState, useEffect, useContext, lazy, Suspense } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, Book, User, FileText, Filter, X, ChevronDown, ChevronUp, ThumbsUp, ThumbsDown, Loader, Eye, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';
import AuthContext from '../../contexts/AuthContext';

// Lazy load the DocumentViewer component
const DocumentViewer = lazy(() => import('../../components/DocumentViewer'));

const SearchResultsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useContext(AuthContext);
  const query = searchParams.get('q') || '';
  const type = searchParams.get('type') || 'all';
  
  const [results, setResults] = useState({
    courses: [],
    professors: [],
    materials: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(type);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    semester: searchParams.get('semester') || '',
    year: searchParams.get('year') || '',
    materialType: searchParams.get('materialType') || '',
    subscriptionsOnly: searchParams.get('subscriptionsOnly') === 'true' || false
  });
  
  // Document viewer state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [currentMaterial, setCurrentMaterial] = useState(null);
  

  useEffect(() => {
    const fetchSearchResults = async () => {
        if (!query) {
            setLoading(false);
            return;
          }
          
        // Get schoolId from the correct location in the user object
        const schoolId = user?.school?.id;
        
        if (!schoolId) {
        setError('You must be logged in and have a school selected to search.');
        setLoading(false);
        return;
        }
      
      try {
        setLoading(true);
        
        // Debug output
        console.log('API URL:', process.env.REACT_APP_API_URL);
        console.log('SchoolID:', user.schoolId);
        
        // Build search URL with params
        const apiBaseUrl = process.env.REACT_APP_API_URL || '';
        const searchUrl = new URL(`${apiBaseUrl}/api/search`);
        searchUrl.searchParams.append('q', query);
        searchUrl.searchParams.append('schoolId', user.school.id);
        
        if (type !== 'all') {
          searchUrl.searchParams.append('type', type);
        }
        
        // Log the full URL for debugging
        console.log('Search URL:', searchUrl.toString());
        
        // Add filters if they're set
        Object.entries(filters).forEach(([key, value]) => {
          if (value) {
            searchUrl.searchParams.append(key, value);
          }
        });
        
        const response = await fetch(searchUrl, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch search results');
        }
        
        const data = await response.json();
        
        // If the response has 'results' property (advanced search), handle differently
        if (data.results) {
          if (type === 'courses') {
            setResults({ courses: data.results, professors: [], materials: [] });
          } else if (type === 'professors') {
            setResults({ courses: [], professors: data.results, materials: [] });
          } else if (type === 'materials') {
            setResults({ courses: [], professors: [], materials: data.results });
          }
        } else {
          setResults(data);
        }
      } catch (err) {
        console.error('Search error:', err);
        setError(err.message);
        toast.error('Failed to perform search');
      } finally {
        setLoading(false);
      }
    };

    fetchSearchResults();
    }, [query, type, user, filters]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const newQuery = formData.get('query');
    
    if (!newQuery.trim()) return;
    
    // Update search params
    const newParams = { q: newQuery };
    if (activeTab !== 'all') {
      newParams.type = activeTab;
    }
    
    // Add active filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        newParams[key] = value;
      }
    });
    
    setSearchParams(newParams);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    
    setFilters((prev) => ({
      ...prev,
      [name]: value
    }));
    
    // Update URL params with new filter
    const newParams = new URLSearchParams(searchParams);
    
    if (value) {
      newParams.set(name, value);
    } else {
      newParams.delete(name);
    }
    
    setSearchParams(newParams);
  };

  const clearFilters = () => {
    setFilters({
      semester: '',
      year: '',
      materialType: '',
      subscriptionsOnly: false
    });
    
    // Remove filter params from URL
    const newParams = new URLSearchParams();
    newParams.set('q', query);
    if (activeTab !== 'all') {
      newParams.set('type', activeTab);
    }
    
    setSearchParams(newParams);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    
    // Update URL
    const newParams = new URLSearchParams();
    newParams.set('q', query);
    
    if (tab !== 'all') {
      newParams.set('type', tab);
    }
    
    // Add active filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      }
    });
    
    setSearchParams(newParams);
  };

  // Function to handle viewing a material
  const handleViewMaterial = (material) => {
    // Track the view
    trackMaterialView(material.id);
    
    // Debug: Log material data to compare with full-screen version
    console.log('Modal material data:', material);
    
    // Set the current material and open the viewer
    setCurrentMaterial(material);
    setViewerOpen(true);
  };
  
  // Function to track material views
  const trackMaterialView = async (materialId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/materials/${materialId}/view`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        console.log(`Successfully tracked view for material ${materialId}`);
      } else {
        console.error('Failed to track material view:', await response.text());
      }
    } catch (error) {
      console.error('Error tracking material view:', error);
    }
  };
  
  // Function to close the document viewer
  const handleCloseViewer = () => {
    setViewerOpen(false);
    setCurrentMaterial(null);
  };

  const getTotalResults = () => {
    return results.courses.length + results.professors.length + results.materials.length;
  };

  const getActiveTabResults = () => {
    switch (activeTab) {
      case 'courses':
        return results.courses;
      case 'professors':
        return results.professors;
      case 'materials':
        return results.materials;
      default:
        return [];
    }
  };

  const getSemesters = () => {
    return ['Fall', 'Spring', 'Summer', 'Winter'];
  };

  const getCurrentYear = () => new Date().getFullYear();
  
  const getYears = () => {
    const currentYear = getCurrentYear();
    return Array.from({ length: 10 }, (_, i) => currentYear - i);
  };

  const getMaterialTypes = () => {
    return [
      { value: 'notes', label: 'Notes' },
      { value: 'exam', label: 'Exam' },
      { value: 'syllabus', label: 'Syllabus' },
      { value: 'other', label: 'Other' }
    ];
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Search Results</h1>
      
      {/* Search Form */}
      <div className="mb-8">
        <form onSubmit={handleSearchSubmit} className="flex">
          <div className="relative flex-grow">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              name="query"
              defaultValue={query}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-md leading-5 bg-white dark:bg-gray-700 placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
              placeholder="Search for courses, professors, materials..."
            />
          </div>
          <button
            type="submit"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-r-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            Search
          </button>
        </form>
      </div>
      
      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => handleTabChange('all')}
              className={`${
                activeTab === 'all'
                  ? 'border-red-500 text-red-600 dark:text-red-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              } whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm`}
            >
              All ({getTotalResults()})
            </button>
            <button
              onClick={() => handleTabChange('courses')}
              className={`${
                activeTab === 'courses'
                  ? 'border-red-500 text-red-600 dark:text-red-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              } whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm`}
            >
              Courses ({results.courses.length})
            </button>
            <button
              onClick={() => handleTabChange('professors')}
              className={`${
                activeTab === 'professors'
                  ? 'border-red-500 text-red-600 dark:text-red-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              } whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm`}
            >
              Professors ({results.professors.length})
            </button>
            <button
              onClick={() => handleTabChange('materials')}
              className={`${
                activeTab === 'materials'
                  ? 'border-red-500 text-red-600 dark:text-red-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              } whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm`}
            >
              Materials ({results.materials.length})
            </button>
          </nav>
        </div>
      </div>
      
      {/* Filters */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
          >
            <Filter className="h-4 w-4 mr-2" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
            {showFilters ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />}
          </button>
          
          {Object.values(filters).some(Boolean) && (
            <button
              onClick={clearFilters}
              className="flex items-center text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
            >
              <X className="h-4 w-4 mr-1" />
              Clear Filters
            </button>
          )}
        </div>
        
        {showFilters && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="flex items-center p-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="subscriptionsOnly"
                  checked={filters.subscriptionsOnly}
                  onChange={(e) => {
                    const { name, checked } = e.target;
                    setFilters((prev) => ({
                      ...prev,
                      [name]: checked
                    }));
                    
                    // Update URL params
                    const newParams = new URLSearchParams(searchParams);
                    if (checked) {
                      newParams.set(name, 'true');
                    } else {
                      newParams.delete(name);
                    }
                    setSearchParams(newParams);
                  }}
                  className="h-4 w-4 rounded border-gray-300 dark:border-gray-500 text-red-600 focus:ring-red-500 dark:bg-gray-600 dark:focus:ring-red-400"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200 select-none">
                  My Subscribed Classes Only
                </span>
              </label>
            </div>
            
            {activeTab === 'materials' && (
              <>
                <div>
                  <label htmlFor="semester" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Semester
                  </label>
                  <select
                    id="semester"
                    name="semester"
                    value={filters.semester}
                    onChange={handleFilterChange}
                    className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">All Semesters</option>
                    {getSemesters().map((sem) => (
                      <option key={sem} value={sem}>
                        {sem}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label htmlFor="year" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Year
                  </label>
                  <select
                    id="year"
                    name="year"
                    value={filters.year}
                    onChange={handleFilterChange}
                    className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">All Years</option>
                    {getYears().map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label htmlFor="materialType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Material Type
                  </label>
                  <select
                    id="materialType"
                    name="materialType"
                    value={filters.materialType}
                    onChange={handleFilterChange}
                    className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">All Types</option>
                    {getMaterialTypes().map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}
          </div>
        )}
      </div>
      
      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-gray-100"></div>
        </div>
      )}
      
      {/* Error State */}
      {error && !loading && (
        <div className="bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error! </strong>
          <span className="block sm:inline">{error}</span>
        </div>
      )}
      
      {/* No Results State */}
      {!loading && !error && getTotalResults() === 0 && query && (
        <div className="text-center py-12">
          <div className="mx-auto h-12 w-12 text-gray-400">
            <Search className="h-12 w-12" />
          </div>
          <h3 className="mt-2 text-lg font-medium text-gray-900 dark:text-white">No results found</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            We couldn't find anything matching "{query}". Try adjusting your search terms or filters.
          </p>
        </div>
      )}
      
      {/* Results Display */}
      {!loading && !error && getTotalResults() > 0 && (
        <div className="space-y-8">
          {/* All Results View */}
          {activeTab === 'all' && (
            <>
              {/* Courses Section */}
              {results.courses.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                      Courses ({results.courses.length})
                    </h2>
                    {results.courses.length > 3 && (
                      <button
                        onClick={() => handleTabChange('courses')}
                        className="text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                      >
                        View All
                      </button>
                    )}
                  </div>
                  
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                    <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                      {results.courses.slice(0, 3).map((course) => (
                        <li key={course.id} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                          <Link to={`/courses/${course.id}`} className="block">
                            <div className="px-4 py-4 sm:px-6">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 bg-red-100 dark:bg-red-900 p-2 rounded-md">
                                  <Book className="h-5 w-5 text-red-600 dark:text-red-400" />
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                                    {course.code}: {course.name}
                                  </div>
                                  <div className="text-sm text-gray-500 dark:text-gray-400">
                                    {course.departmentName} ({course.departmentCode})
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
              
              {/* Professors Section */}
              {results.professors.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                      Professors ({results.professors.length})
                    </h2>
                    {results.professors.length > 3 && (
                      <button
                        onClick={() => handleTabChange('professors')}
                        className="text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                      >
                        View All
                      </button>
                    )}
                  </div>
                  
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                    <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                      {results.professors.slice(0, 3).map((professor) => (
                        <li key={professor.id} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                          <Link to={`/professors/${professor.id}`} className="block">
                            <div className="px-4 py-4 sm:px-6">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 bg-gray-200 dark:bg-gray-700 p-2 rounded-full">
                                  <User className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                                    {professor.name}
                                  </div>
                                  <div className="text-sm text-gray-500 dark:text-gray-400">
                                    {professor.avgRating ? (
                                      <div className="flex items-center">
                                        <span className="text-yellow-500">★</span>
                                        <span className="ml-1">{professor.avgRating.toFixed(1)}</span>
                                        <span className="ml-1">({professor.ratingCount} {professor.ratingCount === 1 ? 'review' : 'reviews'})</span>
                                      </div>
                                    ) : (
                                      'No ratings yet'
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
              
              {/* Materials Section */}
              {results.materials.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                      Materials ({results.materials.length})
                    </h2>
                    {results.materials.length > 3 && (
                      <button
                        onClick={() => handleTabChange('materials')}
                        className="text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                      >
                        View All
                      </button>
                    )}
                  </div>
                  
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                    <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                      {results.materials.slice(0, 3).map((material) => (
                        <li key={material.id} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                          <div 
                            onClick={() => handleViewMaterial(material)}
                            className="block cursor-pointer"
                          >
                            <div className="px-4 py-4 sm:px-6">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 bg-blue-100 dark:bg-blue-900 p-2 rounded-md">
                                  <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                                    {material.title}
                                  </div>
                                  <div className="text-sm text-gray-500 dark:text-gray-400">
                                    {material.courseCode}: {material.courseName} - {material.semester} {material.year}
                                  </div>
                                  <div className="mt-1 flex items-center">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 capitalize">
                                      {material.type}
                                    </span>
                                    <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                                      {new Date(material.createdAt).toLocaleDateString()}
                                    </span>
                                    <div className="ml-4 flex items-center space-x-2">
                                      <button className="text-blue-600 dark:text-blue-400 flex items-center text-xs">
                                        <Eye className="h-3 w-3 mr-1" />
                                        View
                                      </button>
                                      <Link 
                                        to={`/materials/${material.id}`} 
                                        className="text-green-600 dark:text-green-400 flex items-center text-xs hover:text-green-700 dark:hover:text-green-300"
                                        title="Open in full screen"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <ExternalLink className="h-3 w-3 mr-1" />
                                        Full Screen
                                      </Link>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </>
          )}
          
          {/* Single Tab View */}
          {activeTab !== 'all' && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
              {getActiveTabResults().length > 0 ? (
                <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                  {activeTab === 'courses' && 
                    results.courses.map((course) => (
                      <li key={course.id} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                        <Link to={`/courses/${course.id}`} className="block">
                          <div className="px-4 py-4 sm:px-6">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 bg-red-100 dark:bg-red-900 p-2 rounded-md">
                                <Book className="h-5 w-5 text-red-600 dark:text-red-400" />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {course.code}: {course.name}
                                </div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  {course.departmentName} ({course.departmentCode})
                                </div>
                                {course.description && (
                                  <div className="mt-1 text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                                    {course.description}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </Link>
                      </li>
                    ))
                  }
                  
                  {activeTab === 'professors' && 
                    results.professors.map((professor) => (
                      <li key={professor.id} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                        <Link to={`/professors/${professor.id}`} className="block">
                          <div className="px-4 py-4 sm:px-6">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 bg-gray-200 dark:bg-gray-700 p-2 rounded-full">
                                <User className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {professor.name}
                                </div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  {professor.avgRating ? (
                                    <div className="flex items-center">
                                      <span className="text-yellow-500">★</span>
                                      <span className="ml-1">{professor.avgRating.toFixed(1)}</span>
                                      <span className="ml-1">({professor.ratingCount} {professor.ratingCount === 1 ? 'review' : 'reviews'})</span>
                                    </div>
                                  ) : (
                                    'No ratings yet'
                                  )}
                                </div>
                                {professor.bio && (
                                  <div className="mt-1 text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                                    {professor.bio}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </Link>
                      </li>
                    ))
                  }
                  
                  {activeTab === 'materials' && 
                    results.materials.map((material) => (
                      <li key={material.id} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                        <div 
                          onClick={() => handleViewMaterial(material)}
                          className="block cursor-pointer"
                        >
                          <div className="px-4 py-4 sm:px-6">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 bg-blue-100 dark:bg-blue-900 p-2 rounded-md">
                                <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                              </div>
                              <div className="ml-4 flex-grow">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {material.title}
                                </div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  {material.courseCode}: {material.courseName} - {material.semester} {material.year}
                                </div>
                                <div className="mt-1 flex items-center">
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 capitalize">
                                    {material.type}
                                  </span>
                                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                                    {new Date(material.createdAt).toLocaleDateString()}
                                  </span>
                                  <div className="ml-4 flex items-center space-x-2">
                                    <button className="text-blue-600 dark:text-blue-400 flex items-center text-xs">
                                      <Eye className="h-3 w-3 mr-1" />
                                      View
                                    </button>
                                    <Link 
                                      to={`/materials/${material.id}`} 
                                      className="text-green-600 dark:text-green-400 flex items-center text-xs hover:text-green-700 dark:hover:text-green-300"
                                      title="Open in full screen"
                                      onClick={(e) => e.stopPropagation()} // Prevent triggering the parent div's onClick
                                    >
                                      <ExternalLink className="h-3 w-3 mr-1" />
                                      Full Screen
                                    </Link>
                                  </div>
                                </div>
                                {material.description && (
                                  <div className="mt-1 text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                                    {material.description}
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center">
                                <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center">
                                  <ThumbsUp className="h-4 w-4 mr-1" />
                                  {material.upvotes || 0}
                                </div>
                                <div className="ml-2 text-sm text-gray-500 dark:text-gray-400 flex items-center">
                                  <ThumbsDown className="h-4 w-4 mr-1" />
                                  {material.downvotes || 0}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))
                  }
                </ul>
              ) : (
                <div className="px-4 py-5 sm:px-6 text-center">
                  <p className="text-gray-500 dark:text-gray-400">
                    No {activeTab} found matching your search criteria.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* Document viewer modal */}
      {viewerOpen && currentMaterial && (
        <Suspense fallback={
          <div className="fixed inset-0 flex items-center justify-center">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-8 shadow-xl">
              <Loader className="h-12 w-12 text-red-500 animate-spin" />
              <p className="mt-4 text-gray-700 dark:text-gray-300">Loading document viewer...</p>
            </div>
          </div>
        }>
          <DocumentViewer
            fileUrl={currentMaterial.fileUrl}
            fileType={currentMaterial.fileType}
            title={currentMaterial.title}
            token={localStorage.getItem('token')}
            materialId={currentMaterial.id}
            currentUser={user}
            onClose={handleCloseViewer}
            sidebarWidth={256}
          />
        </Suspense>
      )}
    </div>
  );
};

export default SearchResultsPage;